<script setup lang="ts">
import { useRouter } from 'vue-router';
import Lucide from '@/components/Base/Lucide';
import type { Icon } from '@/components/Base/Lucide/icon.types';
import { useAuth } from '@/composables/useAuth';
import { useAuthStore } from '@/stores/auth';
import { Slideover } from '@/components/Base/Headless';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void;
}

const _props = defineProps<Props>();
const emit = defineEmits<Emits>();

const router = useRouter();
const { logout } = useAuth();

const authStore = useAuthStore();

const menuItems: Array<{
  title: string;
  icon: Icon;
  route: string;
  description: string;
}> = [
  {
    title: 'Update Profile',
    icon: 'Users',
    route: 'Profile',
    description: 'Manage your personal information',
  },
  {
    title: 'Security Settings',
    icon: 'Lock',
    route: 'Security',
    description: 'Password, MFA, and security options',
  },
];

const closeDrawer = () => {
  emit('update:isOpen', false);
};

const navigateTo = (routeName: string) => {
  router.push({ name: routeName });
  closeDrawer();
};

const handleLogout = () => {
  logout();
  closeDrawer();
};
</script>

<template>
  <Slideover :open="isOpen" @close="closeDrawer">
    <Slideover.Panel class="w-80 max-w-[90vw]">
      <!-- Header -->
      <div class="bg-gradient-to-r from-theme-1 to-theme-2 p-6 text-white pt-[var(--safe-area-inset-top)]">
        <!-- User Info -->
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20">
            <img src="/src/assets/images/users/user8-50x50.jpg" alt="Profile" class="w-full h-full object-cover" />
          </div>
          <div>
            <p class="font-medium">John Doe</p>
            <p class="text-sm text-white/80">{{ authStore.email }}</p>
          </div>
          <button @click="closeDrawer" class="p-2 rounded-full hover:bg-white/20 transition-colors">
            <Lucide icon="X" class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Menu Items -->
      <div class="py-4">
        <div class="px-6 mb-4">
          <h3 class="text-sm font-medium text-slate-500 uppercase tracking-wide">Account Settings</h3>
        </div>

        <nav class="space-y-1">
          <button
            v-for="item in menuItems"
            :key="item.title"
            @click="navigateTo(item.route)"
            class="w-full px-6 py-4 flex items-start space-x-3 hover:bg-slate-50 transition-colors text-left"
          >
            <div class="flex-shrink-0 w-5 h-5 mt-0.5">
              <Lucide :icon="item.icon" class="w-5 h-5 text-slate-600" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-slate-900">
                {{ item.title }}
              </p>
              <p class="text-xs text-slate-500 mt-1">
                {{ item.description }}
              </p>
            </div>
            <div class="flex-shrink-0">
              <Lucide icon="ChevronRight" class="w-4 h-4 text-slate-400" />
            </div>
          </button>
        </nav>
      </div>

      <!-- Divider -->
      <div class="border-t border-slate-200"></div>

      <!-- Logout -->
      <div class="py-2">
        <nav class="space-y-1">
          <button @click="handleLogout" class="w-full px-6 py-4 flex items-center space-x-3 hover:bg-red-50 transition-colors text-left group">
            <Lucide icon="LogOut" class="w-5 h-5 text-red-600 group-hover:text-red-700" />
            <span class="text-sm font-medium text-red-600 group-hover:text-red-700"> Log Out </span>
          </button>
        </nav>
      </div>
    </Slideover.Panel>
  </Slideover>
</template>
